// ShopPage — everything shop- and deal-shaped that clients see in the app, in one tab.
//
// Owner, 2026-11-01: "Rename tab that says Shop picks and just name it Shop. and organize everything
// shop and deals related in there." So this page gathers what used to be spread out:
//   Window notes  app_bulletins placement 'window' — the notes taped to the window on Home
//   Shop banners  app_bulletins placement 'shop'   — the banners across the top of the Shop screen
//   Add-ons       addon_catalog + organizations.allow_shop (moved here from Settings → Add-ons)
//   Picks         the platform's affiliate shelf (PicksPage) — platform admin only, as before
//
// Notes and banners are rows, not app builds: an operator posts a deal here and every client sees
// it the next time they open the app. The design words come from sql/095 (bulletinDesigns.js).
import { useCallback, useEffect, useRef, useState } from "react";
import { SUPABASE_URL, SUPABASE_ANON_KEY, getAuthToken } from "../lib/supabase";
import { useIsMobile } from "../lib/useIsMobile";
import { BulletinEditor } from "./BulletinEditor";
import { AddonCatalogEditor } from "./AddonCatalogEditor";
import { PicksPage } from "./PicksPage";
import { CAPACITY, statusOf } from "./bulletinDesigns";

const H = () => ({ apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` });

// The nav icon — a little storefront, drawn like the rest of Icon.js (24-px stroke).
export function ShopIcon({ size = 16 }) {
  return (
    <span style={{ width: size, height: size, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" style={{ display: "block" }}>
        <path d="M3 9l1.5-5h15L21 9" /><path d="M3 9a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0" /><path d="M5 12v8h14v-8" /><path d="M10 20v-5h4v5" />
      </svg>
    </span>
  );
}

const TAB_KEY = "shop.tab";
// The last Settings → Shop jump already applied. App keeps the jump in state, so without this
// every later visit to the tab would be thrown back to that section.
let lastJump = 0;

export function ShopPage({ org, onOrgSaved, isPlatformAdmin, showToast, jump }) {
  const isMobile = useIsMobile();
  const SECTIONS = [
    ["window", "Window notes"],
    ["shop", "Shop banners"],
    ["addons", "Add-ons"],
    ...(isPlatformAdmin ? [["picks", "Picks"]] : []),
  ];
  const [tab, setTab] = useState(() => { try { return localStorage.getItem(TAB_KEY) || "window"; } catch { return "window"; } });
  // The open editor's unsaved edits die with its section, so leaving one asks first.
  const dirtyRef = useRef(false);
  const pick = (t) => {
    if (t !== section && dirtyRef.current && !window.confirm("Discard your unsaved changes?")) return;
    dirtyRef.current = false;
    setTab(t); try { localStorage.setItem(TAB_KEY, t); } catch { /* private mode */ }
  };
  // Settings → Add-ons points here; a jump opens that section, once.
  useEffect(() => { if (jump && jump.section && jump.t !== lastJump) { lastJump = jump.t; pick(jump.section); } }, [jump]); // eslint-disable-line react-hooks/exhaustive-deps
  // Picks is the platform's; anyone else who lands on it (a stored tab, a revoked flag) gets the window.
  const section = SECTIONS.some(([k]) => k === tab) ? tab : "window";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await fetch(`${SUPABASE_URL}/rest/v1/app_bulletins?select=*&order=sort.asc`, { headers: H() });
      if (!r.ok) throw new Error(String(r.status));
      const d = await r.json();
      setRows(Array.isArray(d) ? d : []);
      setLoadErr(false);
    } catch { setLoadErr(true); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // allow_shop is the operator's own switch (only customer_app_enabled and billing_mode are
  // platform-set, sql/071). Optimistic, then the org row is re-read.
  const [allowShop, setAllowShop] = useState(!!org?.allow_shop);
  const [shopBusy, setShopBusy] = useState(false);
  useEffect(() => { setAllowShop(!!org?.allow_shop); }, [org?.allow_shop]);
  async function toggleShop() {
    if (!org?.id || shopBusy) return;
    const next = !allowShop;
    setShopBusy(true); setAllowShop(next);
    try {
      const r = await fetch(`${SUPABASE_URL}/rest/v1/organizations?id=eq.${org.id}`, {
        method: "PATCH", headers: { ...H(), "Content-Type": "application/json", Prefer: "return=representation" }, body: JSON.stringify({ allow_shop: next }),
      });
      if (!r.ok) throw new Error(String(r.status));
      // A PATCH that RLS filters out answers 200 with no rows — that's a failure, not a save.
      const d = await r.json().catch(() => null);
      if (!Array.isArray(d) || !d.length) throw new Error("not_saved");
      showToast && showToast(next ? "Add-ons are on — clients can add them from Plan in the app." : "Add-ons are off — clients no longer see them.");
      onOrgSaved && onOrgSaved();
    } catch {
      setAllowShop(!next);
      showToast && showToast("Couldn't change that. Nothing changed.", "error");
    }
    setShopBusy(false);
  }

  const now = Date.now();
  const sum = (p) => {
    const l = rows.filter((r) => (r.placement || "window") === p);
    const live = l.filter((r) => statusOf(r, now) === "live").length;
    return { live, shown: Math.min(live, CAPACITY[p]), scheduled: l.filter((r) => statusOf(r, now) === "scheduled").length, off: l.filter((r) => !r.active).length };
  };
  const stats = { window: sum("window"), shop: sum("shop") };

  const appOn = org?.customer_app_enabled === true;

  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 13.5, color: "var(--text2)", marginBottom: 14, maxWidth: 720, lineHeight: 1.5 }}>
        What clients see in the app for deals and extras — notes on their window, banners in the Shop, and add-ons.
        Post or change one here and clients see it the next time they open the app. <strong style={{ color: "var(--text)", fontWeight: 600 }}>No app update needed.</strong>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: 16 }}>
        <Metric label="On the window" value={loading ? "—" : `${stats.window.shown}`} unit={`/ ${CAPACITY.window}`} accent
                sub={loadErr ? "Couldn't load" : stats.window.live > CAPACITY.window ? `${stats.window.live - CAPACITY.window} live but not shown` : `${stats.window.scheduled} scheduled · ${stats.window.off} off`}
                warn={stats.window.live > CAPACITY.window} onClick={() => pick("window")} />
        <Metric label="Shop banners" value={loading ? "—" : `${stats.shop.shown}`} unit={`/ ${CAPACITY.shop}`}
                sub={loadErr ? "Couldn't load" : stats.shop.live > CAPACITY.shop ? `${stats.shop.live - CAPACITY.shop} live but not shown` : stats.shop.shown ? `${stats.shop.scheduled} scheduled · ${stats.shop.off} off` : "None live — the app's “contact us” card leads"}
                warn={stats.shop.live > CAPACITY.shop} onClick={() => pick("shop")} />
        <Metric label="Add-ons for clients" value={allowShop ? "On" : "Off"} ok={allowShop}
                sub={allowShop ? "Clients add them from Plan" : "Hidden in the app"} onClick={() => pick("addons")} />
        <Metric label="Client app" value={appOn ? "On" : "Not yet"} ok={appOn}
                sub={appOn ? "Clients can sign in" : "What you post waits until it's on"} />
      </div>

      <div className="seg" role="tablist" aria-label="Shop sections"
           style={isMobile ? { marginBottom: 16, display: "flex", width: "100%", maxWidth: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch", scrollbarWidth: "none" } : { marginBottom: 18 }}>
        {SECTIONS.map(([k, label]) => (
          <button key={k} role="tab" aria-selected={section === k} className={section === k ? "on" : ""} onClick={() => pick(k)}
                  style={isMobile ? { whiteSpace: "nowrap", flexShrink: 0, padding: "9px 13px", minHeight: 40 } : undefined}>
            {label}
            {k === "picks" ? <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".04em" }}>PLATFORM</span> : null}
          </button>
        ))}
      </div>

      {(section === "window" || section === "shop") && loadErr ? (
        <div className="card" style={{ padding: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Couldn't load your notes and banners.</div>
          <div style={{ fontSize: 13, color: "var(--text2)", marginTop: 6 }}>Nothing was read, so this is a failed load — not an empty list. Whatever is posted is still live.</div>
          <button className="btn btn-ghost" style={{ marginTop: 12 }} onClick={() => { setLoading(true); load(); }}>Try again</button>
        </div>
      ) : (section === "window" || section === "shop") && loading ? (
        <div style={{ padding: 20, color: "var(--text2)" }}>Loading…</div>
      ) : section === "window" || section === "shop" ? (
        <BulletinEditor key={section} placement={section} rows={rows} setRows={setRows} org={org} showToast={showToast} onReload={load} onDirty={(d) => { dirtyRef.current = d; }} />
      ) : null}

      {section === "addons" ? (
        <div style={{ display: "grid", gap: 14, maxWidth: 820 }}>
          <div className="card" style={{ padding: isMobile ? 14 : 18 }}>
            <button type="button" role="switch" aria-checked={allowShop} disabled={shopBusy || !org?.id} onClick={toggleShop}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, width: "100%", minHeight: 44, padding: 0, border: "none", background: "transparent", font: "inherit", textAlign: "left", cursor: shopBusy ? "wait" : "pointer" }}>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 15, fontWeight: 600, color: "var(--text)" }}>Show add-ons to clients</span>
                <span style={{ display: "block", fontSize: 12.5, color: "var(--text2)", marginTop: 3, lineHeight: 1.45 }}>
                  {allowShop
                    ? "On — clients can add the add-ons you've switched on below from Plan in the app."
                    : "Off — the app shows no add-ons at all, even ones switched on below."}
                  {" "}Window notes and Shop banners aren't affected by this switch.
                </span>
              </span>
              <span aria-hidden="true" style={{ width: 44, height: 26, flexShrink: 0, borderRadius: 13, background: allowShop ? "var(--ok)" : "rgba(0,0,0,.14)", position: "relative", transition: "background .2s", opacity: shopBusy ? .6 : 1 }}>
                <span style={{ width: 22, height: 22, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: allowShop ? 20 : 2, transition: "left .2s", boxShadow: "0 1px 2px rgba(0,0,0,.25)" }} />
              </span>
            </button>
          </div>
          <div className="card" style={{ padding: isMobile ? 14 : 18 }}>
            <AddonCatalogEditor org={org} showToast={showToast} />
          </div>
        </div>
      ) : null}

      {section === "picks" && isPlatformAdmin ? <PicksPage showToast={showToast} /> : null}
    </div>
  );
}

function Metric({ label, value, unit, sub, accent, ok, warn, onClick }) {
  const color = warn ? "#8A5A06" : accent ? "var(--accent)" : ok === true ? "var(--ok)" : "var(--text)";
  const Tag = onClick ? "button" : "div";
  return (
    <Tag type={onClick ? "button" : undefined} onClick={onClick} className="stat-card"
         style={{ textAlign: "left", font: "inherit", cursor: onClick ? "pointer" : "default", padding: "14px 16px", width: "100%", ...(warn ? { borderColor: "#F6D88A", background: "#FFFCF4" } : null) }}>
      <div className="stat-label" style={{ marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, letterSpacing: "-.02em", lineHeight: 1, color }}>
        {value}{unit ? <span style={{ fontSize: 14, fontWeight: 500, color: "var(--text2)", marginLeft: 4 }}>{unit}</span> : null}
      </div>
      <div style={{ fontSize: 11.5, color: warn ? "#8A5A06" : "var(--text2)", marginTop: 6, lineHeight: 1.35 }}>{sub}</div>
    </Tag>
  );
}
